import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import NavigationBtnControl from 'components/BookingForm/NavigationBtnControl/NavigationBtnControl';
import InsuranceUpload from 'components/BookingForm/InsuranceBookingConfirmation/InsuranceUploadForm/InsuranceUpload/InsuranceUpload';
import { renderToast } from 'components/CustomToast/CustomToast';
import { updateBookingInsurance } from 'services/APIs';
import { loadOtpToken } from 'utils/storage';
import { useAppSelector } from 'redux/reduxHooks';

import styles from 'components/BookingForm/InsuranceBookingConfirmation/InsuranceBookingConfirmation.module.scss';

interface IInsuranceUploadFormValues {
  fileFront: File | null;
  fileBack: File | null;
}

interface IInsuranceUploadForm {
  bookingIds: string[];
  onDone: () => void;
}

const InsuranceUploadForm = (props: IInsuranceUploadForm) => {
  const { bookingIds, onDone } = props;

  const { setValue, watch, handleSubmit } = useForm<IInsuranceUploadFormValues>({
    mode: 'all',
    defaultValues: {
      fileFront: null,
      fileBack: null,
    },
  });

  const { fileFront, fileBack } = watch();

  const isCardImageMissing = !fileFront || !fileBack;

  const [isLoading, setIsLoading] = useState(false);

  const {
    familyBookingSlice: {
      step1: { phoneNumber },
    },
    clinicDetailsSlice: {
      clinicData: { isInsuranceRequired },
    },
    authSlice: { tempAccessToken },
  } = useAppSelector((state) => state);

  const buildFormData = (bookingId: string, data: IInsuranceUploadFormValues) => {
    const formData = new FormData();

    formData.append('bookingId', bookingId);

    if (data.fileFront) {
      formData.append('insuranceFront', data.fileFront);
    }

    if (data.fileBack) {
      formData.append('insuranceBack', data.fileBack);
    }

    return formData;
  };

  const onSubmit = async (data: IInsuranceUploadFormValues) => {
    try {
      setIsLoading(true);

      await Promise.all(
        bookingIds.map((bookingId) =>
          updateBookingInsurance(buildFormData(bookingId, data), {
            otpToken: loadOtpToken(phoneNumber),
            tmpAccessToken: tempAccessToken,
          })
        )
      );

      onDone();
    } catch (error) {
      renderToast({
        message: 'We could not save your insurance card. Please try again later',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={styles['upload-instruction']}>
        <h3>Insurance Card</h3>

        <p>
          Ensure the image is bright, in focus, and fully shows all fields on the
          card so the clinic can verify your benefits accurately.
        </p>
      </div>

      <div className={styles['upload-section']}>
        <div>
          <InsuranceUpload
            id={'insurance-front'}
            file={fileFront}
            setFile={(value) =>
              setValue('fileFront', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />

          <span>Front</span>
        </div>

        <div>
          <InsuranceUpload
            id={'insurance-back'}
            file={fileBack}
            setFile={(value) =>
              setValue('fileBack', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />

          <span>Back</span>
        </div>
      </div>

      <NavigationBtnControl
        nextBtnTitle="Done"
        onNext={handleSubmit(onSubmit)}
        isDisabledNextBtn={isInsuranceRequired && isCardImageMissing}
        isLoading={isLoading}
      />
    </>
  );
};

export default InsuranceUploadForm;
